// shared/accent.ts: one builder accent in, every shade the app needs out (design system A, accent-led; docs/SPEC.md 2.8).
// A builder configures a logo and one accent color. homebuild has no hue of its own: text is warm ink and greys, and the
// accent colors the interactive and state elements (primary buttons, selection, counts, the tracker, links, focus).
// Everything else (hover, pressed, the text-safe accent, the color on top of it, the pale tints and the faint wash) is
// derived here in OKLCH, so a light accent stays readable and a dark one still shows a hover. Pure functions, shared by
// the app (src/lib/theme.ts sets the results as --brand-accent-* on <html>) and the Worker (the offline page, emails).

export type Rgb = [number, number, number]; // sRGB, 0..1
export type Oklch = [number, number, number]; // L 0..1, C 0..~0.37, H degrees

/** homebuild's warm ink: headings and strong text, the dark text on a light accent, and the accent when none is set. */
export const INK = "#1A1917";
export const INK_HOVER = "#2A2926";
export const WHITE = "#FFFFFF";
export const PAGE = "#FFFFFF";
/** The quiet panel color (--color-surface): links and marks must also read on it. */
export const SURFACE = "#F7F7F5";
/** Meta text (--color-text-subtle): it must read on the accent's tint and wash too. */
export const META = "#6C6964";

export function hexToRgb(hex: string): Rgb {
  const h = hex.replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.slice(0, 6);
  const n = Number.parseInt(full, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export function rgbToHex(rgb: Rgb): string {
  return `#${rgb.map((c) => Math.round(Math.min(1, Math.max(0, c)) * 255).toString(16).padStart(2, "0")).join("").toUpperCase()}`;
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

export function rgbToOklch(rgb: Rgb): Oklch {
  const [r, g, b] = rgb.map(toLinear) as Rgb;
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const C = Math.hypot(A, B);
  const H = C < 1e-4 ? 0 : ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360;
  return [L, C, H];
}

function oklchToLinear([L, C, H]: Oklch): Rgb {
  const a = C * Math.cos((H * Math.PI) / 180);
  const b = C * Math.sin((H * Math.PI) / 180);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

/** OKLCH to sRGB, lowering chroma (never lightness or hue) until the color fits sRGB. */
export function oklchToRgb(c: Oklch): Rgb {
  const inGamut = (rgb: Rgb) => rgb.every((v) => v >= -1e-4 && v <= 1 + 1e-4);
  let [L, C, H] = c;
  L = Math.min(1, Math.max(0, L));
  let lin = oklchToLinear([L, C, H]);
  if (!inGamut(lin)) {
    let lo = 0;
    let hi = C;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut(oklchToLinear([L, mid, H]))) lo = mid;
      else hi = mid;
    }
    C = lo;
    lin = oklchToLinear([L, C, H]);
  }
  return lin.map((v) => toGamma(Math.min(1, Math.max(0, v)))) as Rgb;
}

export const oklchHex = (c: Oklch) => rgbToHex(oklchToRgb(c));

/** WCAG 2 relative luminance and contrast ratio. */
export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
}

/** Mixes `top` over `base` at `alpha` (sRGB), as a solid color. */
export function over(top: string, base: string, alpha: number): string {
  const t = hexToRgb(top);
  const b = hexToRgb(base);
  return rgbToHex(t.map((v, i) => v * alpha + b[i]! * (1 - alpha)) as Rgb);
}

/** Darkens (in OKLCH lightness, same hue) until the color reaches `ratio` against every background. */
export function darkenTo(hex: string, ratio: number, against: string[]): string {
  let [L, C, H] = rgbToOklch(hexToRgb(hex));
  let out = hex.toUpperCase();
  for (let i = 0; i < 100 && against.some((bg) => contrast(out, bg) < ratio); i++) {
    L -= 0.01;
    out = oklchHex([L, C, H]);
  }
  return out;
}

export const isHexColor = (v: unknown): v is string => typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v.trim());

export type AccentTokens = {
  /**
   * The fill: primary buttons, selected chips and controls, counts, the tracker's marks, the Add Photos tab. The builder's
   * color exactly, unless neither white nor ink reaches 4.5:1 on it; then the same hue, darkened until white does.
   */
  accent: string;
  /** Primary button hover: 0.06 OKLCH lightness toward more contrast with `on` (lighter under ink or on a very dark
   *  fill, L < 0.40; darker under white otherwise). Never under 4.5:1 with `on`. */
  hover: string;
  /** Pressed: the same direction, 0.10. */
  press: string;
  /** The accent as text and marks (links, the current stage's label, active icons): at least 4.5:1 on white, the
   *  panel and the accent's own tint. */
  strong: string;
  /** Text and icons on the fill: white when it reaches 4.5:1, else homebuild ink. */
  on: string;
  /** Secondary text on the fill: `on` at 72% over the fill, raised until it reaches 4.5:1. */
  onMuted: string;
  /** Pale washes of the accent's hue: the selected row, the active nav item, the current stage's halo (L 0.965), and the
   *  focus halo (L 0.925). Text on them stays ink. */
  tint: string;
  tint2: string;
  /** The faintest wash, for one quiet panel per screen (Next Step, the team's stats strip): L 0.978, chroma <= 0.012. */
  wash: string;
};

/** A builder with no accent (or one that is not a hex color) gets homebuild's warm ink: homebuild has no hue. */
export function accentTokens(input?: string | null): AccentTokens {
  const raw = (input ?? "").trim();
  const given = isHexColor(raw) ? raw.toUpperCase() : INK;
  let accent = given;
  let on = WHITE;
  if (contrast(WHITE, given) < 4.5) {
    if (contrast(INK, given) >= 4.5) on = INK;
    else accent = darkenTo(given, 4.5, [WHITE]);
  }
  const [L, C, H] = rgbToOklch(hexToRgb(accent));
  const step = (d: number, dir: number) => oklchHex([L + d * dir, C, H]);
  let dir = on === INK || L < 0.4 ? 1 : -1;
  if (contrast(on, step(0.06, dir)) < 4.5) dir = -dir;
  let alpha = 0.72;
  while (alpha < 1 && contrast(over(on, accent, alpha), accent) < 4.5) alpha += 0.02;
  const tint = oklchHex([0.965, Math.min(C * 0.22, 0.022), H]);
  return {
    accent,
    hover: accent === INK ? INK_HOVER : step(0.06, dir),
    press: step(0.1, dir),
    strong: darkenTo(given, 4.5, [PAGE, SURFACE, tint]),
    on,
    onMuted: over(on, accent, Math.min(alpha, 1)),
    tint,
    tint2: oklchHex([0.925, Math.min(C * 0.4, 0.04), H]),
    wash: oklchHex([0.978, Math.min(C * 0.16, 0.012), H]),
  };
}

/** The tokens as CSS custom properties, in the order theme.ts sets them. */
export function accentProperties(input?: string | null): [string, string][] {
  const t = accentTokens(input);
  return [
    ["--brand-accent", t.accent],
    ["--brand-accent-hover", t.hover],
    ["--brand-accent-press", t.press],
    ["--brand-accent-strong", t.strong],
    ["--brand-accent-on", t.on],
    ["--brand-accent-on-muted", t.onMuted],
    ["--brand-accent-tint", t.tint],
    ["--brand-accent-tint-2", t.tint2],
    ["--brand-accent-wash", t.wash],
  ];
}
